import { CustomFieldImportFragment, MappableImportInput, Maybe } from 'common/src/generated/types';
import { isNonEmptyArray } from 'common/src/util/array';
import { FormApi } from 'final-form';
import { groupBy } from 'lodash-es';
import * as React from 'react';
import { MappingsFormBox } from './mappingsFormBox';
import { PresetsFormBox } from './presetsFormBox';
import { ImportFieldCategory } from './types';
import { useCSVHeader } from './useCSVHeader';

export type StoredMappings = {
    [key: string]: Maybe<string>;
};

interface IMappingsFormBoxes<T> {
    additionalCategories?: ImportFieldCategory[];
    customFields: CustomFieldImportFragment[];
    defaultFields: Record<string, string>;
    file: File;
    form: FormApi<{ input: T }>;
    modeSelection?: React.ReactNode;
    requiredFieldSlugs: string[];
    values: { input: T };

    onToggle?(isOpen: boolean): void;
}

export function MappingsFormBoxes<T extends MappableImportInput>({
    additionalCategories,
    customFields,
    defaultFields,
    file,
    form,
    modeSelection,
    onToggle,
    requiredFieldSlugs,
    values
}: IMappingsFormBoxes<T>) {
    const { isLoading, columns, errors } = useCSVHeader(file, {
        delimiter: values.input.csv.delimiter ?? undefined,
        quoteChar: values.input.csv.quoteChar ?? undefined,
        newline: (values.input.csv.newline as any) ?? undefined
    });

    const categories = React.useMemo(() => {
        const customFieldsCategories: ImportFieldCategory[] = Object.entries(
            groupBy(customFields, (cf) => cf.customFieldCategoryId)
        ).map(([_categoryId, fields]) => {
            const field = fields[0];

            return {
                id: field.customFieldCategoryId,
                name: field.customFieldCategoryName,
                fields: fields
            };
        });

        return additionalCategories
            ? customFieldsCategories.concat(additionalCategories)
            : customFieldsCategories;
    }, [additionalCategories, customFields]);

    if (isLoading || errors) {
        return null; /* Loading doesn’t take long: don’t display a loader. Errors are aldready displayed in the <FileS3Input />. */
    } else {
        return (
            <>
                <MappingsFormBox
                    categories={categories}
                    columns={columns}
                    defaultFields={defaultFields}
                    modeSelection={modeSelection}
                    requiredFieldSlugs={requiredFieldSlugs}
                    values={values.input.mappings}
                />

                {isNonEmptyArray(customFields) && (
                    <PresetsFormBox
                        change={form.change}
                        customFields={customFields}
                        file={file}
                        values={values.input.presets}
                        onToggle={onToggle}
                    />
                )}
            </>
        );
    }
}
